import { useEffect, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useLabels, useLayerTree } from "../hooks";
import type { TreeRow, TreeScope } from "../layerTreeModel";
import { EmptyState } from "../primitives/EmptyState";
import { LayerGroup } from "./LayerGroup";
import { LayerRow } from "./LayerRow";

export type LayerTreeProps = {
  root: TreeScope;
  onTable?: ((row: TreeRow) => void) | null;
  filter?: string;
  label: string;
  excludeKeys?: string[];
  dataFxmap?: string;
  className?: string;
  emptyText?: string | null;
};

const typeaheadResetMs = 600;

export function LayerTree({ root, filter = "", label, excludeKeys = [], dataFxmap = "layer-tree", className, emptyText = null, onTable = null }: LayerTreeProps) {
  const labels = useLabels();
  const tree = useLayerTree(root, filter, excludeKeys);
  const itemRefs = useRef(new Map<number, HTMLLIElement>());
  const typeahead = useRef({ text: "", at: 0 });
  const pendingFocus = useRef<number | null>(null);
  const { rows, activeIndex } = tree;

  useEffect(() => {
    const index = pendingFocus.current;

    if (index === null) return;

    pendingFocus.current = null;
    itemRefs.current.get(index)?.focus();
  });

  const focusIndex = (index: number) => {
    if (index < 0 || index >= rows.length) return;

    tree.setActiveIndex(index);
    pendingFocus.current = index;
    itemRefs.current.get(index)?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLLIElement>, index: number, row: TreeRow) => {
    if (event.target !== event.currentTarget && event.target instanceof HTMLElement && event.target.matches("input[type='range'], input[type='text'], input[type='search'], textarea")) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(tree.move(1));
        return;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(tree.move(-1));
        return;
      case "ArrowRight":
        event.preventDefault();
        event.stopPropagation();

        if (row.isGroup && !row.expanded) tree.setCollapsed(row.collapseKey, false);
        else if (row.isGroup) focusIndex(index + 1);

        return;
      case "ArrowLeft":
        event.preventDefault();
        event.stopPropagation();

        if (row.isGroup && row.expanded) tree.setCollapsed(row.collapseKey, true);
        else focusIndex(tree.parentOf(index));

        return;
      case "Home":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(tree.first());
        return;
      case "End":
        event.preventDefault();
        event.stopPropagation();
        focusIndex(tree.last());
        return;
      case " ":
      case "Enter":
        if (event.target !== event.currentTarget) return;

        event.preventDefault();
        event.stopPropagation();
        tree.toggleRow(row);
        return;
      default:
        break;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      const current = typeahead.current;
      const text = now - current.at < typeaheadResetMs ? current.text + event.key : event.key;

      typeahead.current = { text, at: now };

      const found = tree.typeahead(text);

      if (found >= 0) {
        event.preventDefault();
        focusIndex(found);
      }
    }
  };

  const renderFrom = (start: number, level: number): [ReactNode[], number] => {
    const nodes: ReactNode[] = [];
    let index = start;

    while (index < rows.length && rows[index].level === level) {
      const row = rows[index];
      const rowIndex = index;

      index++;

      let children: ReactNode = null;

      if (row.isGroup && row.expanded && index < rows.length && rows[index].level > level) {
        const [childNodes, next] = renderFrom(index, level + 1);

        children = (
          <ul role="group" className="fxmap-tree-children">
            {childNodes}
          </ul>
        );
        index = next;
      }

      const tabIndex = activeIndex === rowIndex || (activeIndex < 0 && rowIndex === 0) ? 0 : -1;

      nodes.push(
        <li
          key={row.id}
          ref={element => {
            if (element === null) itemRefs.current.delete(rowIndex);
            else itemRefs.current.set(rowIndex, element);
          }}
          role="treeitem"
          className={["fxmap-tree-item", row.isGroup ? "fxmap-tree-item-group" : "fxmap-tree-item-leaf"].join(" ")}
          aria-level={row.level}
          aria-expanded={row.isGroup ? row.expanded : undefined}
          aria-checked={row.isGroup && row.single ? undefined : row.state === "some" ? "mixed" : row.state === "all"}
          data-fxmap-layer={row.id}
          tabIndex={tabIndex}
          style={{ "--fxmap-tree-level": row.level } as Record<string, number>}
          onFocus={event => {
            if (event.target === event.currentTarget) tree.setActiveIndex(rowIndex);
          }}
          onKeyDown={event => onKeyDown(event, rowIndex, row)}
        >
          {row.isGroup ? (
            <LayerGroup row={row} onToggle={() => tree.toggleRow(row)} onExpand={expanded => tree.setCollapsed(row.collapseKey, !expanded)} />
          ) : row.snapshot !== null ? (
            <LayerRow snapshot={row.snapshot} entry={row.entry} single={row.single} onToggle={() => tree.toggleRow(row)} onTable={onTable !== null && row.entry.source?.hasTable ? () => onTable(row) : null} />
          ) : null}
          {children}
        </li>
      );
    }

    return [nodes, index];
  };

  if (rows.length === 0) {
    if (emptyText === "") return null;

    const text = emptyText ?? (filter.trim().length > 0 ? labels.noResults : labels.noLayers);

    return <EmptyState icon={filter.trim().length > 0 ? "search" : "layers"} title={text} className="fxmap-tree-empty" />;
  }

  const [items] = renderFrom(0, 1);

  return (
    <ul role="tree" aria-label={label} className={["fxmap-tree", className ?? ""].filter(Boolean).join(" ")} data-fxmap={dataFxmap} data-fxmap-root={root}>
      {items}
    </ul>
  );
}
